import { useState, useEffect } from 'react';
import { C } from '../styles/colours.js';
import socket from '../socket.js';
import { QRCodeSVG } from 'qrcode.react';
import { getText } from '../questionBank.js';

function TV({ roomCode, lang = 'en' }) {
  const [room, setRoom] = useState((roomCode || '').toUpperCase());
  const [codeInput, setCodeInput] = useState('');

  const [players, setPlayers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [phase, setPhase] = useState('lobby');
  const [buzzed, setBuzzed] = useState(null);
  const [buzzOrder, setBuzzOrder] = useState([]);
  const [winner, setWinner] = useState(null);

  useEffect(() => {
    if (!room) return;
    socket.emit('join-spectator', room);

    const onPlayers = (p) => setPlayers(p);
    const onStarted = ({ qIndex: qi, questions: qs }) => {
      setQuestions(qs || []); setQIndex(qi || 0); setPhase('reading'); setBuzzed(null); setBuzzOrder([]);
    };
    const onOpen = () => { setPhase('open'); setBuzzed(null); setBuzzOrder([]); };
    const onFirstBuzz = ({ name, buzzOrder: order }) => { setBuzzed({ name }); setBuzzOrder(order || []); setPhase('buzzed'); };
    const onBuzzUpdate = (order) => setBuzzOrder(order || []);
    const onCorrect = ({ players: p }) => { if (p) setPlayers(p); };
    const onNewQuestion = (qi) => { setQIndex(qi); setPhase('reading'); setBuzzed(null); setBuzzOrder([]); };
    const onWinner = (w) => { setWinner(w); setPhase('winner'); };

    socket.on('player-joined', onPlayers);
    socket.on('game-started', onStarted);
    socket.on('buzzers-open', onOpen);
    socket.on('first-buzz', onFirstBuzz);
    socket.on('buzz-update', onBuzzUpdate);
    socket.on('answer-correct', onCorrect);
    socket.on('new-question', onNewQuestion);
    socket.on('winner-revealed', onWinner);

    return () => {
      socket.off('player-joined', onPlayers);
      socket.off('game-started', onStarted);
      socket.off('buzzers-open', onOpen);
      socket.off('first-buzz', onFirstBuzz);
      socket.off('buzz-update', onBuzzUpdate);
      socket.off('answer-correct', onCorrect);
      socket.off('new-question', onNewQuestion);
      socket.off('winner-revealed', onWinner);
    };
  }, [room]);

  const currentQ = questions[qIndex];
  const sortedPlayers = [...players].sort((a, b) => b.pts - a.pts);
  const joinUrl = `${window.location.origin}?room=${room}`;

  const exitTV = () => { window.location.href = window.location.origin + '/'; };
  const exitBtn = (
    <button onClick={exitTV} style={{ position: 'fixed', top: 16, left: 16, zIndex: 50, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: C.textSub, borderRadius: 10, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>✕ Exit</button>
  );

  const wrap = { background: C.bg, minHeight: '100vh', width: '100%', color: C.white, fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4vh 4vw', boxSizing: 'border-box', textAlign: 'center' };

  // ===== CODE ENTRY (opened with no room code) =====
  if (!room) {
    return (
      <div style={wrap}>
        {exitBtn}
        <div style={{ width: 72, height: 72, borderRadius: 18, background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 30, marginBottom: 24 }}>TQ</div>
        <div style={{ fontSize: 'clamp(24px,4vw,40px)', fontWeight: 900, marginBottom: 8 }}>📺 Big screen</div>
        <div style={{ fontSize: 'clamp(14px,2vw,20px)', color: C.textSub, marginBottom: 24, maxWidth: 480, lineHeight: 1.5 }}>Type the room code from the host's screen, then hit Connect.</div>
        <input
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value.toUpperCase().slice(0, 6))}
          onKeyDown={(e) => { if (e.key === 'Enter' && codeInput.trim()) setRoom(codeInput.trim().toUpperCase()); }}
          placeholder="CODE"
          style={{ width: 'min(90vw, 360px)', textAlign: 'center', letterSpacing: 'clamp(6px,2vw,14px)', fontSize: 'clamp(36px,8vw,72px)', fontWeight: 900, background: C.inputBg, border: `2px solid ${C.inputBorder}`, borderRadius: 16, padding: '16px', color: C.gold, outline: 'none', marginBottom: 20, boxSizing: 'border-box' }}
        />
        <button
          onClick={() => setRoom(codeInput.trim().toUpperCase())}
          disabled={!codeInput.trim()}
          style={{ width: 'min(90vw, 360px)', background: codeInput.trim() ? C.gold : 'rgba(255,255,255,0.1)', color: codeInput.trim() ? '#000' : C.textMuted, border: 'none', borderRadius: 14, padding: '16px', fontSize: 18, fontWeight: 800, cursor: codeInput.trim() ? 'pointer' : 'not-allowed' }}
        >
          Connect →
        </button>
      </div>
    );
  }

  if (phase === 'lobby') {
    return (
      <div style={wrap}>
        {exitBtn}
        <div style={{ fontSize: 'clamp(28px,5vw,52px)', fontWeight: 900, letterSpacing: 2, marginBottom: '2vh' }}>Tabli<span style={{ color: C.red }}>Q</span></div>
        <div style={{ fontSize: 'clamp(14px,2vw,20px)', color: C.textSub, marginBottom: '3vh' }}>Scan to join the game</div>
        <div style={{ background: '#fff', borderRadius: 20, padding: 20, marginBottom: '3vh' }}>
          <QRCodeSVG value={joinUrl} size={260} bgColor="#ffffff" fgColor="#07071A" />
        </div>
        <div style={{ fontSize: 'clamp(14px,2vw,20px)', color: C.textMuted }}>Room code</div>
        <div style={{ fontSize: 'clamp(48px,12vw,120px)', fontWeight: 900, letterSpacing: 'clamp(6px,2vw,18px)', color: C.gold, lineHeight: 1 }}>{room}</div>
        <div style={{ fontSize: 'clamp(16px,2.5vw,26px)', color: C.green, fontWeight: 800, marginTop: '3vh' }}>👥 {players.length} joined</div>
      </div>
    );
  }

  if (phase === 'winner') {
    const top3 = sortedPlayers.slice(0, 3);
    return (
      <div style={wrap}>
        {exitBtn}
        <div style={{ fontSize: 'clamp(60px,14vw,140px)', lineHeight: 1 }}>🏆</div>
        <div style={{ fontSize: 'clamp(28px,6vw,64px)', fontWeight: 900, color: C.gold, marginTop: '2vh' }}>{winner?.name || top3[0]?.name}</div>
        <div style={{ fontSize: 'clamp(16px,2.5vw,28px)', color: C.textSub, marginBottom: '4vh' }}>Winner!</div>
        <div style={{ display: 'flex', gap: '2vw', justifyContent: 'center', flexWrap: 'wrap' }}>
          {top3.map((p, i) => (
            <div key={p.name} style={{ background: C.card, border: `2px solid ${i === 0 ? C.gold : i === 1 ? '#C0C0C0' : '#CD7F32'}`, borderRadius: 16, padding: '2vh 3vw', minWidth: 140 }}>
              <div style={{ fontSize: 'clamp(28px,5vw,48px)' }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</div>
              <div style={{ fontSize: 'clamp(16px,2.5vw,28px)', fontWeight: 800 }}>{p.name}</div>
              <div style={{ fontSize: 'clamp(18px,3vw,34px)', fontWeight: 900, color: C.gold }}>{p.pts}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...wrap, justifyContent: 'flex-start', paddingTop: '5vh' }}>
      {exitBtn}
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: 1100, alignItems: 'center', marginBottom: '3vh' }}>
        <div style={{ fontSize: 'clamp(18px,3vw,30px)', fontWeight: 900 }}>Tabli<span style={{ color: C.red }}>Q</span></div>
        <div style={{ fontSize: 'clamp(14px,2vw,22px)', color: C.textMuted }}>Q{qIndex + 1}{questions.length ? ` / ${questions.length}` : ''}</div>
      </div>

      <div style={{ width: '100%', maxWidth: 1100, background: 'rgba(255,209,102,0.07)', border: `2px solid ${C.gold}40`, borderRadius: 24, padding: '5vh 4vw', marginBottom: '3vh' }}>
        <div style={{ fontSize: 'clamp(24px,4.5vw,56px)', fontWeight: 800, lineHeight: 1.3 }}>{currentQ ? getText(currentQ.q, lang) : ''}</div>
      </div>

      {phase === 'reading' && <div style={{ fontSize: 'clamp(18px,3vw,32px)', color: C.textSub }}>🎙️ Get ready…</div>}
      {phase === 'open' && <div style={{ fontSize: 'clamp(22px,4vw,44px)', fontWeight: 900, color: C.green }}>🔔 BUZZERS OPEN!</div>}
      {phase === 'buzzed' && buzzed && (
        <div style={{ width: '100%', maxWidth: 1100 }}>
          <div style={{ fontSize: 'clamp(24px,5vw,52px)', fontWeight: 900, color: C.red }}>🔔 {buzzed.name} buzzed first!</div>
          {buzzOrder.length > 1 && (
            <div style={{ display: 'flex', gap: '1.5vw', justifyContent: 'center', flexWrap: 'wrap', marginTop: '2vh' }}>
              {buzzOrder.slice(0, 3).map((b, i) => (
                <div key={i} style={{ fontSize: 'clamp(16px,2.5vw,26px)', color: C.textSub }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {b.name}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {sortedPlayers.length > 0 && (
        <div style={{ width: '100%', maxWidth: 700, marginTop: '4vh' }}>
          <div style={{ fontSize: 'clamp(12px,1.6vw,16px)', letterSpacing: 2, color: C.textMuted, fontWeight: 700, marginBottom: '1.5vh' }}>LEADERBOARD</div>
          {sortedPlayers.slice(0, 6).map((p, i) => (
            <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '1.4vh 3vw', borderRadius: 12, background: i === 0 ? `${C.gold}18` : C.card, marginBottom: 8 }}>
              <div style={{ fontWeight: 900, fontSize: 'clamp(16px,2.5vw,26px)', color: i === 0 ? C.gold : C.textMuted, width: 30 }}>{i + 1}</div>
              <div style={{ flex: 1, textAlign: 'left', fontSize: 'clamp(16px,2.5vw,26px)', fontWeight: 700 }}>{p.name}</div>
              <div style={{ fontWeight: 900, fontSize: 'clamp(18px,3vw,30px)', color: C.gold }}>{p.pts}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TV;